import { BreakpointObserver } from '@angular/cdk/layout';
import { isPlatformBrowser } from '@angular/common';
import {
  Component,
  Inject,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
} from '@angular/core';
import {
  ActivatedRoute,
  ActivationEnd,
  NavigationEnd,
  NavigationStart,
  Router,
} from '@angular/router';

import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import {
  debounceTime,
  filter,
  takeUntil,
  withLatestFrom,
} from 'rxjs/operators';

import { environment } from '@env/environment';

import { SLICED_DESCRIPTION_HEIGHT } from '@app/core/constants/style-constant';
import { ROUTING_KEY } from '@app/core/enums/routing-key.enum';
import { ToolsHelper } from '@app/core/helpers/tools.helper';
import {
  AmplitudeAbstractService,
  AnalyticsEvents,
  AnalyticsService,
  APP_CONFIG,
  EventsService,
  IAppConfig,
  StorageService,
} from './core/services';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, OnDestroy {
  public readonly appVersion = `v-${environment.version}`;
  public readonly nowDate = new Date().toISOString();
  public readonly isBrowser: boolean;
  public readonly isWhiteLabel: boolean;
  public isFullWidth: boolean;
  public hideContent = false;

  public containerWidth: string = '853px';
  public isBigContainer: boolean = true;
  public coreMain: boolean = false;
  public whiteLabelMain: boolean = false;
  public navigationEnd: boolean = false;
  public hideMap: boolean = false;
  public standalone = false;
  public redirectOnly = false;
  public hideAppLoader = false;
  public iconLoaded = false;
  public isFirstLoadEnded = false;
  public isLoading = false;

  private destroy$: Subject<void> = new Subject();

  constructor(
    @Inject(APP_CONFIG) public readonly appConfig: IAppConfig,
    @Inject(PLATFORM_ID) private readonly platformId: any,
    private translateService: TranslateService,
    private storageService: StorageService,
    private router: Router,
    private analyticsService: AnalyticsService,
    private amplitudeService: AmplitudeAbstractService,
    public eventsService: EventsService,
    private readonly breakpointObserver: BreakpointObserver,
    private readonly route: ActivatedRoute,
  ) {
    this.eventsService.setIsFirstLoading(true);
    this.isBrowser = isPlatformBrowser(this.platformId);
    this.isWhiteLabel = this.appConfig.settings.is_white_label;
    this.eventsService.isFirstLoading$
      .pipe(takeUntil(this.destroy$), debounceTime(300))
      .subscribe(val => {
        setTimeout(() => {
          this.isFirstLoadEnded = !val;
        });
      });
    if (this.isBrowser) {
      this.eventsService.isLoading$
        .pipe(takeUntil(this.destroy$))
        .subscribe(val => {
          if (val) {
            setTimeout(() => {
              this.isLoading = val;
            });
          } else {
            window.setTimeout(() => {
              this.isLoading = false;
            });
          }
        });
    }

    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        this.redirectOnly = this.isRedirectRoute(this.router.url);
        const activePage =
          this.route.snapshot.children?.map(item => item.data?.page) ?? [];
        this.hideAppLoader = activePage.includes(
          ROUTING_KEY.STANDALONE_PASSPORT_CHECK_IN,
        );
        this.standalone =
          activePage.includes(ROUTING_KEY.STANDALONE_PASSPORT_CHECK_IN) ||
          activePage.includes(ROUTING_KEY.STANDALONE_EVENT) ||
          activePage.includes(ROUTING_KEY.STANDALONE_PASSPORT) ||
          activePage.includes(ROUTING_KEY.RESUBSCRIBE_REMINDER) ||
          activePage.includes(ROUTING_KEY.UNSUBSCRIBE_REMINDER);
      });
    this.redirectOnly = this.isRedirectRoute(this.router.url);
    this.storageService.setPlaces(null);
    this.eventsService.hideMap$
      .pipe(takeUntil(this.destroy$))
      .subscribe(val => {
        this.hideMap = val;
      });

    this.router.events
      .pipe(
        filter(event => event instanceof ActivationEnd),
        withLatestFrom(this.route.fragment),
        withLatestFrom(this.breakpointObserver.observe('(max-width: 768px)')),
        takeUntil(this.destroy$),
      )
      .subscribe(
        ([[event, fragment], isMobile]: [[ActivationEnd, any], any]) => {
          if (
            [
              ROUTING_KEY.STATIC_PAGE,
              ROUTING_KEY.YOUR_PASSPORT_LIST,
              ROUTING_KEY.PASSPORT,
              ROUTING_KEY.EVENT,
            ].includes(event.snapshot.data?.page) ||
            (event.snapshot.data?.page === ROUTING_KEY.EVENT_LIST &&
              event.snapshot.children?.filter(
                item => item.data.page === ROUTING_KEY.EVENT,
              )?.length > 0)
          ) {
            this.eventsService.setHideMap(true);
            if (!isMobile?.matches) {
              this.hideContent = false;
            }
            this.eventsService.showMobileMapButton$.next(false);
            this.eventsService.openMobileMap$.next(false);
            return;
          }
          if (
            fragment &&
            event.snapshot.data?.page !== ROUTING_KEY.STATIC_PAGE
          ) {
            this.eventsService.setHideMap(false);
            if (!isMobile?.matches) {
              this.hideContent = true;
            }
            this.eventsService.showMobileMapButton$.next(true);
            this.eventsService.openMobileMap$.next(true);
            return;
          }

          if (!isMobile?.matches) {
            this.hideContent = false;
          }
          this.eventsService.showMobileMapButton$.next(true);
          this.eventsService.setHideMap(false);
          this.eventsService.openMobileMap$.next(false);
        },
      );

    if (this.isBrowser) {
      this.whiteLabelMain = this.isWhiteLabel && location.pathname === '/';

      const setHeight = (): void => {
        document.documentElement.style.setProperty(
          '--innerHeight',
          window.innerHeight + 'px',
        );
      };
      document.documentElement.style.setProperty(
        '--descriptionSlicedHeight',
        SLICED_DESCRIPTION_HEIGHT + 'px',
      );
      window.addEventListener('resize', setHeight);
      setHeight();
    }

    this.amplitudeService.init();
    const is_registered = !!this.storageService.TOKEN;
    this.amplitudeService.setItems([
      { key: 'white_label', val: this.appConfig.settings.config_name },
      { key: 'is_registered', val: is_registered },
    ]);

    this.analyticsService.emitEvent(AnalyticsEvents.sessionStart, {
      is_registered,
    });
    this.appendGaTrackingCode();
    this.translateService.use('en');
    this.translateService.setDefaultLang('en');
  }

  ngOnInit(): void {
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationStart),
        takeUntil(this.destroy$),
      )
      .subscribe((event: NavigationStart) => {
        this.navigationEnd = false;
        this.redirectOnly = this.isRedirectRoute(event.url);
        this.coreMain = !this.isWhiteLabel && event.url === '/';
        this.whiteLabelMain = this.isWhiteLabel && event.url === '/';
        if (this.coreMain) {
          this.containerWidth = '100%';
          this.isFullWidth = true;
          return;
        }
      });
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        this.navigationEnd = true;
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
  }

  bgLoaded(event): void {
    if (event) {
      this.iconLoaded = true;
    }
  }

  getImage(): any {
    if (this.appConfig.configsUrl) {
      return {
        'background-image':
          `url(` +
          ToolsHelper.minLogoUrl(
            this.appConfig.configsUrl,
            this.appConfig.settings.logo,
          ) +
          '?v=' +
          this.nowDate +
          `)`,
        'background-size': 'cover',
      };
    }
    return {
      'background-image': 'url(/assets/img/no-photo.svg)',
      'background-size': 'cover',
    };
  }

  checkOnLoaded(): boolean {
    let exists = false;
    if (this.iconLoaded) {
      exists = true;
    }
    return exists;
  }

  toggleContainer(): void {
    this.eventsService.setRouteMapState(this.hideContent);
  }

  private isRedirectRoute(url: string): boolean {
    const normalizedUrl = url.split('?')[0].split('#')[0];

    return (
      normalizedUrl === '/redirect' || normalizedUrl.startsWith('/redirect/')
    );
  }

  private appendGaTrackingCode(): void {
    // if (location.hostname !== 'localhost') {
    //   try {
    //     const script = document.createElement('script');
    //     script.innerHTML = `
    //     (function(i,s,o,g,r,a,m){i['GoogleAnalyticsObject']=r;i[r]=i[r]||function(){
    //     (i[r].q=i[r].q||[]).push(arguments)},i[r].l=1*new Date();a=s.createElement(o),
    //     m=s.getElementsByTagName(o)[0];a.async=1;a.src=g;m.parentNode.insertBefore(a,m)
    //     })(window,document,'script','https://www.google-analytics.com/analytics.js','ga');
    //
    //     ga('create', '` + environment.GOOGLE_ANALYTICS_KEY + `', 'auto');
    //   `;
    //     document.head.appendChild(script);
    //   } catch (ex) {
    //     console.error('Error appending google analytics');
    //     console.error(ex);
    //   }
    // }
  }

  protected readonly ToolsHelper = ToolsHelper;
}
